import { connectSession } from "./session";
import { CCP_STATES, LOG_PREFIX, STATE_UPDATE_RING_MEDIA_DEVICE } from "../../constants/constants";
import { STATE_AGENT, STATE_LOGIN } from "../constants";
import { changeAgentState } from "../api/agent";
import Emitter from "../../emitter";
import {
  API_CALL_MUTE,
  TELEMETRY_INFO,
  TELEMETRY_ERROR,
  STATE_UPDATE_SPEAKER_DEVICE,
  STATE_UPDATE_MICROPHONE_DEVICE,
  AUDIO_DEVICE_CHANGED,
  AUDIO_DEVICE_ADDED,
  AUDIO_DEVICE_REMOVED,
} from "../../constants/events";
import { showErrorToast } from "../../utils/utils";
import storeRTK from "../../store/storeRTK";

const lp = LOG_PREFIX + "[handlers.agent.ts]: ";

// Missed contact tracking is now centralized in contact.ts

function handleAgentRefresh(agent: any) {
  console.log(lp + "Agent Refreshed", agent);
  connectSession.updateCCPstateToBg(CCP_STATES.CONNECTED);
}

// We will log in the browsers console any new agent status
function handleAgentStateChange(stateObj: {
  agent: any;
  newState: string;
  oldState: string;
}) {
  
  try {
    const stateType = (
      stateObj.agent.getState() ||
      stateObj.agent.getStatus() ||
      {}
    ).type;
    let { newState } = stateObj;

    // Missed contact tracking logic is now in contact.ts

    // Existing logic for state type handling
    if (
      (stateType === "system" || stateType === "System") &&
      newState !== "AfterCallWork"
    ) {
      return;
    }

    const nextState = stateObj?.agent?.getNextState();
    if (nextState && nextState?.name !== newState) {
      if (
        (nextState?.type === "system" || nextState?.type === "System") &&
        nextState?.name !== "AfterCallWork"
      ) {
        // Ignore
      } else {
        console.log(
          lp + "Found next state changing new state to next State",
          newState,
          nextState
        );
        newState = nextState.name;
      }
    }
    Emitter.emit(STATE_AGENT, {
      id: newState,
      displayName: newState,
      stateModifiedDate: new Date().getTime(),
    });
  } catch (error) {
    Emitter.emit(TELEMETRY_ERROR, {
      type: "notif",
      chan: TELEMETRY_INFO,
      body: {
        type: STATE_AGENT,
        info:
          `${lp} Error in changing agent state to UI from ${stateObj?.oldState} to ${stateObj?.newState}` +
          error,
      },
    });
    console.log(
      `${lp} Error in changing agent state to UI from ${stateObj?.oldState} to ${stateObj?.newState}`,
      error
    );
  }
}

function handleAgentNextStateChange(agent: any) {
  const nextState = agent?.getNextState();
  const currentStateName = agent?.getState()?.name;
  try {
    if (nextState && nextState?.name !== "AfterCallWork") {
      if (
        (nextState?.type === "system" || nextState?.type === "System") &&
        nextState?.name !== "AfterCallWork" 
      ) {
        // Ignore
        return
      } else {
        console.log(
          lp + "Found next state changing new state to next State",
          currentStateName,
          nextState?.name
        );
      }
    }
    
    Emitter.emit(STATE_AGENT, {
      id: nextState?.name,
      displayName: nextState?.name,
      stateModifiedDate: new Date().getTime(),
    });
  } catch (error) {
    Emitter.emit(TELEMETRY_ERROR, {
      type: "notif",
      chan: TELEMETRY_INFO,
      body: {
        type: STATE_AGENT,
        info:
          `${lp} Error in changing agent next state to UI from ${currentStateName} to ${nextState?.name}` +
          error,
      },
    });
    console.log(
      `${lp} Error in changing agent next state to UI from ${currentStateName} to ${nextState?.name}`,
      error
    );
  }
}

function handleSoftphoneError(error: any) {
  const logPrefix = lp + "handleSoftphoneError: ";
  console.error(logPrefix + "Softphone error occurred:", error);

  // Emit telemetry for tracking softphone errors
  Emitter.emit(TELEMETRY_ERROR, {
    type: "notif",
    chan: TELEMETRY_INFO,
    body: {
      type: "SOFTPHONE_ERROR",
      info: logPrefix + "Softphone error occurred",
      error: error,
      timestamp: new Date().toISOString(),
    },
  });
  if(error?.errorType === 'signalling_handshake_failure') {
    //console.error(logPrefix + "Softphone error occurred:", error);
    showErrorToast('Softphone reconnect failed. Please refresh or wait for a moment.', "softphoneReconnectFailed");
    return;
  }
}

export function subscribeToAgentEvents(agent: any) {
  // Subscribing to Streams API Long Polling Agent events
  connectSession.agent = agent;
  var config = agent.getConfiguration();
  console.log(lp + "Agent Configuration", config);
  // Emit login event for agent state tracking
  Emitter.emit(STATE_LOGIN, {
    body: { ...config, agentLoginTime: new Date().toISOString() },
    success: true,
  });
  agent.onRefresh(handleAgentRefresh);
  agent.onStateChange(handleAgentStateChange);
  agent.onEnqueuedNextState(handleAgentNextStateChange);
  agent.onSoftphoneError(handleSoftphoneError);
  //agent.onInitFailed(handleSoftphoneError);
  agent.onError((err: any) => {
    console.error('agent error', err, err.message);
  });
  const currState = agent.getStatus();
  if (currState && currState.name && currState.name === "Offline") {
    changeAgentStateToAvailable();
  }
  agent.onMuteToggle(
    function (obj: any) {
      const contact = agent.getContacts(connect.ContactType.VOICE)[0];
      if (obj.muted) {
        console.log(lp + "agent is muted successfully during contact", contact);
      } else {
        console.log(lp + "agent is unmuted during contact ", contact);
      }
    },
    function (error: any) {
      const c = agent.getContacts(connect.ContactType.VOICE);
      let contact = null;
      if (c.length > 0) {
        contact = c[0];
      }
      console.log(lp + "Error in mute handler", error, contact);
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: API_CALL_MUTE,
          info: lp + "Error in mute handler",
          error,
          contact,
        },
      });
    }
  );
  // Helper function to get device label from stored media devices
  const getDeviceLabelById = (deviceId: string): string => {
    const mediaDevices = storeRTK.getState().webrtc.mediaDevices;
    if (!mediaDevices || !Array.isArray(mediaDevices)) return "Unknown";
    const device = mediaDevices.find((d: any) => d.deviceId === deviceId);
    return device?.label || device?.groupId || "Unknown";
  };

  agent.onRingerDeviceChanged((obj: any) => {
    console.log(lp + "Ringer device changed to:", obj?.deviceId, obj);
    
    // Get current store state for comparison
    const currentRingerId = storeRTK.getState().webrtc.ringtoneOutoutDeviceId;
    
    // Skip if device hasn't actually changed (prevents duplicate telemetry)
    if (currentRingerId === obj?.deviceId) {
      console.log(lp + "Ringer device unchanged, skipping telemetry");
      return;
    }
    
    const deviceLabel = obj?.deviceId ? getDeviceLabelById(obj.deviceId) : "Unknown";
    
    if (obj?.deviceId) {
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: STATE_UPDATE_RING_MEDIA_DEVICE,
          info: lp + `onRingerDeviceChanged : Ringer device for user ${agent.getConfiguration().username} set to: ${deviceLabel} (${obj.deviceId})`,
          deviceId: obj.deviceId,
          deviceLabel: deviceLabel,
          previousDeviceId: currentRingerId,
          previousDeviceLabel: currentRingerId ? getDeviceLabelById(currentRingerId) : "None",
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      });
      Emitter.emit(STATE_UPDATE_RING_MEDIA_DEVICE, {
        success: true,
        body: { deviceId: obj.deviceId }
      });
    } else {
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: STATE_UPDATE_RING_MEDIA_DEVICE,
          info: lp + `onRingerDeviceChanged : Ringer device for user ${agent.getConfiguration().username} failed to set`,
          deviceId: obj?.deviceId,
          previousDeviceId: currentRingerId,
          error: "Device ID is undefined or null",
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      });
    }
  });
  
  agent.onSpeakerDeviceChanged((obj: any) => {
    console.log(lp + "Speaker device changed to:", obj?.deviceId, obj);
    
    // Get current store state for comparison
    const currentSpeakerId = storeRTK.getState().webrtc.speakerDeviceId;
    
    // Skip if device hasn't actually changed (prevents duplicate telemetry)
    if (currentSpeakerId === obj?.deviceId) {
      console.log(lp + "Speaker device unchanged, skipping telemetry");
      return;
    }
    
    const deviceLabel = obj?.deviceId ? getDeviceLabelById(obj.deviceId) : "Unknown";
    
    if (obj?.deviceId) {
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: STATE_UPDATE_SPEAKER_DEVICE,
          info: lp + `onSpeakerDeviceChanged : Speaker device for user ${agent.getConfiguration().username} set to: ${deviceLabel} (${obj.deviceId})`,
          deviceId: obj.deviceId,
          deviceLabel: deviceLabel,
          previousDeviceId: currentSpeakerId,
          previousDeviceLabel: currentSpeakerId ? getDeviceLabelById(currentSpeakerId) : "None",
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      });
      Emitter.emit(STATE_UPDATE_SPEAKER_DEVICE, {
        success: true,
        body: { deviceId: obj.deviceId }
      });
    } else {
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: STATE_UPDATE_SPEAKER_DEVICE,
          info: lp + `onSpeakerDeviceChanged : Speaker device for user ${agent.getConfiguration().username} failed to set`,
          deviceId: obj?.deviceId,
          previousDeviceId: currentSpeakerId,
          error: "Device ID is undefined or null",
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      });
    }
  });

  //To properly set a microphone device for every softphone call, you can call this API in the callback function passed to onLocalMediaStreamCreated API.
  agent.onLocalMediaStreamCreated(() => {
    const deviceId = storeRTK.getState().webrtc.microphoneDeviceId;
    console.log(lp + "Local media stream created, setting microphone device to:", deviceId);
    if (deviceId) {
      agent.setMicrophoneDevice(deviceId);
    }
  });
 
  agent.onMicrophoneDeviceChanged((obj: {deviceId: string, device: any}) => {
    console.log(lp + "Microphone device changed to:", obj?.deviceId, obj);
    
    // Get current store state for comparison
    const currentMicId = storeRTK.getState().webrtc.microphoneDeviceId;
    
    // Skip if device hasn't actually changed (prevents duplicate telemetry)
    if (currentMicId === obj?.deviceId) {
      console.log(lp + "Microphone device unchanged, skipping telemetry");
      return;
    }
    
    const deviceLabel = obj?.deviceId ? getDeviceLabelById(obj.deviceId) : "Unknown";
    
    if (obj?.deviceId) {
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: STATE_UPDATE_MICROPHONE_DEVICE,
          info: lp + `onMicrophoneDeviceChanged : Microphone device for user ${agent.getConfiguration().username} set to: ${deviceLabel} (${obj.deviceId})`,
          deviceId: obj.deviceId,
          deviceLabel: deviceLabel,
          previousDeviceId: currentMicId,
          previousDeviceLabel: currentMicId ? getDeviceLabelById(currentMicId) : "None",
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      }); 
      Emitter.emit(STATE_UPDATE_MICROPHONE_DEVICE, {
        success: true,
        body: { deviceId: obj.deviceId }
      });
    } else {
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: STATE_UPDATE_MICROPHONE_DEVICE,
          info: lp + `onMicrophoneDeviceChanged : Microphone device for user ${agent.getConfiguration().username} failed to set`,
          deviceId: obj?.deviceId,
          previousDeviceId: currentMicId,
          error: "Device ID is undefined or null",
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      }); 
    }
  });

  // Register browser-level device change listener for comprehensive telemetry
  registerBrowserDeviceChangeListener(agent);
}

/**
 * Registers a listener for browser-level audio device changes
 * This captures device plug/unplug events that Amazon Connect may not report
 */
function registerBrowserDeviceChangeListener(agent: any) {
  if (!navigator.mediaDevices || !navigator.mediaDevices.addEventListener) {
    console.warn(lp + "MediaDevices API not available for device change detection");
    return;
  }

  let previousDevices: MediaDeviceInfo[] = [];
  let isInitialized = false;

  // Get initial device list
  navigator.mediaDevices.enumerateDevices().then((devices) => {
    previousDevices = devices.filter(d => d.kind === "audioinput" || d.kind === "audiooutput");
    isInitialized = true;
    console.log(lp + "Initial device list captured:", previousDevices.length, "devices");
  }).catch((err) => {
    console.error(lp + "Error getting initial device list:", err);
  });

  // Listen for device changes
  navigator.mediaDevices.addEventListener("devicechange", async () => {
    // Skip if initial device list hasn't loaded yet to avoid false positives
    if (!isInitialized) {
      console.log(lp + "Skipping devicechange event - initial device list not ready");
      return;
    }

    try {
      const currentDevices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = currentDevices.filter(d => d.kind === "audioinput" || d.kind === "audiooutput");
      
      const previousCount = previousDevices.length;
      const currentCount = audioDevices.length;
      
      // Determine what changed
      const addedDevices = audioDevices.filter(
        (current) => !previousDevices.some((prev) => prev.deviceId === current.deviceId)
      );
      const removedDevices = previousDevices.filter(
        (prev) => !audioDevices.some((current) => current.deviceId === prev.deviceId)
      );

      // Get current selected devices from store
      const webrtcState = storeRTK.getState().webrtc;
      
      // Determine event type
      const eventType = addedDevices.length > 0 
        ? AUDIO_DEVICE_ADDED 
        : removedDevices.length > 0 
          ? AUDIO_DEVICE_REMOVED 
          : AUDIO_DEVICE_CHANGED;

      // Emit comprehensive telemetry
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: eventType,
          info: lp + `Browser devicechange event detected`,
          username: agent?.getConfiguration?.()?.username || "unknown",
          action: addedDevices.length > 0 ? "added" : removedDevices.length > 0 ? "removed" : "changed",
          previousDeviceCount: previousCount,
          currentDeviceCount: currentCount,
          addedDevices: addedDevices.map((d) => ({ 
            deviceId: d.deviceId, 
            label: d.label, 
            kind: d.kind 
          })),
          removedDevices: removedDevices.map((d) => ({ 
            deviceId: d.deviceId, 
            label: d.label, 
            kind: d.kind 
          })),
          currentSelectedMicrophone: webrtcState.microphoneDeviceId,
          currentSelectedSpeaker: webrtcState.speakerDeviceId,
          currentSelectedRinger: webrtcState.ringtoneOutoutDeviceId,
          allCurrentDevices: audioDevices.map((d) => ({
            deviceId: d.deviceId,
            label: d.label,
            kind: d.kind,
          })),
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      });

      console.log(lp + "Browser device change detected:", {
        added: addedDevices.length,
        removed: removedDevices.length,
        total: currentCount,
      });

      // Update previous devices for next comparison
      previousDevices = audioDevices;
    } catch (error) {
      console.error(lp + "Error handling device change:", error);
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: AUDIO_DEVICE_CHANGED,
          info: lp + `Error handling browser devicechange event`,
          error: String(error),
          timestamp: new Date().toISOString(),
          context: {
            agent: true,
          },
        },
      });
    }
  });

  console.log(lp + "Browser device change listener registered");
}

export function resetAfterCallWork() {
  const acwState = {
    state: "COMPLETE",
  };
  return acwState;
}

export function handleAfterCallWork(contact: any) {
  console.log(lp + "Agent is in After Call Work state", contact);
  const acwState = {
    state: "PENDING",
  };

  setTimeout(() => resetAfterCallWork, 5000);
  return acwState;
}

export function changeAgentStateToAvailable() {
  console.log(
    "Calling to set agent status to available if on Contact Reject or Rona",
    "Available"
  );
  if (connectSession?.agent?.getStatus()?.name === "Available") {
    console.log("No change: agent is already on Available state");
    return;
  }
  changeAgentState({ agentState: "Available" });
}
